import {
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { KnowledgeBaseSourceTag } from '@/components/knowledge-base/KnowledgeBaseDocumentIcon'
import { KNOWLEDGE_BASE_GUIDES } from '@/lib/definitions/knowledgeBase'
import {
  findKnowledgeBaseDocument,
  formatKnowledgeBaseKey,
  knowledgeBasePdfUrl,
  knowledgeBasePublicSiteUrl,
  parseKnowledgeBaseKey,
} from '@/lib/misc/knowledgeBase'
import { renderKnowledgeBaseDocument } from '@/lib/misc/knowledgeBaseContent'
import { cn } from '@/lib/misc/Utils'
import {
  useKnowledgeBaseCatalogue,
  useKnowledgeBaseDocument,
} from '@/lib/queries/KnowledgeBase.queries'
import { KnowledgeBaseNotFoundError } from '@/lib/services/KnowledgeBaseService'

import ArrowDownTrayIcon from '@icons/arrow-down-tray'
import ArrowTopRightOnSquareIcon from '@icons/arrow-top-right-on-square'
import ChevronDownIcon from '@icons/chevron-down'
import ClockIcon from '@icons/clock'
import DocumentTextIcon from '@icons/document-text'
import ExclamationTriangleIcon from '@icons/exclamation-triangle'

const WIDE_TOC_THRESHOLD = 2
const SCROLLSPY_OFFSET = 80
const ANCHOR_OFFSET = 60

const formatDate = (value?: string) => {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? undefined
    : date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
}

const ArticleSkeleton = () => (
  <div className="space-y-3 px-6 pt-16 pb-6" aria-busy="true">
    {[
      'w-1/3',
      'h-5.5 w-4/5',
      'w-full',
      'w-11/12',
      'w-2/3',
      'mt-5 h-36 w-full',
    ].map((size) => (
      <div
        key={size}
        className={cn('h-3 animate-pulse rounded-md bg-neutral-10', size)}
      />
    ))}
  </div>
)

interface KnowledgeBaseArticleProps {
  target: string
  scrollContainer: RefObject<HTMLDivElement | null>
  isWide: boolean
  onNavigate: (key: string) => void
  onReplace: (key: string) => void
}

const KnowledgeBaseArticle = ({
  target,
  scrollContainer,
  isWide,
  onNavigate,
  onReplace,
}: KnowledgeBaseArticleProps) => {
  const { path, anchor } = parseKnowledgeBaseKey(target)
  const { documents } = useKnowledgeBaseCatalogue()
  const meta = findKnowledgeBaseDocument(documents, path)
  const { data, error, isLoading } = useKnowledgeBaseDocument(path)
  const [activeHeading, setActiveHeading] = useState<string>()
  const [progress, setProgress] = useState(0)
  const [lightbox, setLightbox] = useState<string>()

  const rendered = useMemo(
    () =>
      data
        ? renderKnowledgeBaseDocument(
            data.markdown,
            path,
            meta?.title ?? path.split('/').pop() ?? path,
            documents
          )
        : undefined,
    [data, path, meta?.title, documents]
  )

  const scrollToAnchor = useCallback(
    (id: string, updateHistory: boolean) => {
      const container = scrollContainer.current
      const element = container?.querySelector<HTMLElement>(
        `[id="${CSS.escape(id)}"]`
      )
      if (!container || !element) return
      const question = element.closest('details')
      if (question) question.open = true
      container.scrollTo({
        top:
          element.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          ANCHOR_OFFSET,
        behavior: 'smooth',
      })
      element.classList.remove('kb-flash')
      void element.offsetWidth
      element.classList.add('kb-flash')
      if (updateHistory) onReplace(formatKnowledgeBaseKey({ path, anchor: id }))
    },
    [scrollContainer, onReplace, path]
  )

  useEffect(() => {
    scrollContainer.current?.scrollTo({ top: 0 })
  }, [path, scrollContainer])

  useEffect(() => {
    if (!rendered || !anchor) return
    const frame = requestAnimationFrame(() => scrollToAnchor(anchor, false))
    return () => cancelAnimationFrame(frame)
  }, [rendered, anchor, scrollToAnchor])

  useEffect(() => {
    const container = scrollContainer.current
    if (!container || !rendered) return
    const onScroll = () => {
      const max = container.scrollHeight - container.clientHeight
      setProgress(max > 0 ? container.scrollTop / max : 0)
      const top = container.getBoundingClientRect().top
      let current = rendered.headings[0]?.id
      for (const { id } of rendered.headings) {
        const element = container.querySelector(`[id="${CSS.escape(id)}"]`)
        if (
          element &&
          element.getBoundingClientRect().top - top < SCROLLSPY_OFFSET
        ) {
          current = id
        }
      }
      setActiveHeading(current)
    }
    onScroll()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => container.removeEventListener('scroll', onScroll)
  }, [rendered, scrollContainer])

  const onContentClick = (event: MouseEvent<HTMLDivElement>) => {
    const element = event.target as HTMLElement
    const link = element.closest<HTMLElement>('[data-kb-key]')
    if (link?.dataset.kbKey) {
      event.preventDefault()
      onNavigate(link.dataset.kbKey)
      return
    }
    const anchorLink = element.closest<HTMLElement>('[data-kb-anchor]')
    if (anchorLink?.dataset.kbAnchor) {
      event.preventDefault()
      scrollToAnchor(anchorLink.dataset.kbAnchor, true)
      return
    }
    if (element instanceof HTMLImageElement) setLightbox(element.src)
  }

  useEffect(() => {
    if (!lightbox) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopImmediatePropagation()
      setLightbox(undefined)
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () =>
      window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [lightbox])

  if (isLoading) return <ArticleSkeleton />

  if (error || !rendered || !data) {
    const notFound = error instanceof KnowledgeBaseNotFoundError
    return (
      <div className="px-5 pt-16">
        <div className="flex items-start gap-3 rounded-xl bg-neutral-10 p-4 text-[13px]">
          <ExclamationTriangleIcon className="mt-0.5 size-4.5 shrink-0 text-status-warning-900" />
          <div className="min-w-0">
            <p className="font-semibold">
              {notFound
                ? 'We couldn’t find that article.'
                : 'The knowledge base is unavailable right now.'}
            </p>
            <p className="mt-0.5 break-all text-neutral-50">
              {notFound ? (
                <code>{path}.md</code>
              ) : (
                'Please try again in a moment.'
              )}
            </p>
            <button
              type="button"
              onClick={() => onNavigate('')}
              className="mt-2.5 font-medium text-accent-100 hover:underline"
            >
              Back to knowledge base home
            </button>
          </div>
        </div>
      </div>
    )
  }

  const category = meta?.category
  const guide = KNOWLEDGE_BASE_GUIDES.map((item) => ({
    ...item,
    steps: item.steps.filter((step) =>
      findKnowledgeBaseDocument(documents, step)
    ),
  })).find((item) => item.steps.length > 1 && item.steps.includes(path))
  const step = guide ? guide.steps.indexOf(path) : -1
  const previous = guide
    ? findKnowledgeBaseDocument(documents, guide.steps[step - 1])
    : undefined
  const next = guide
    ? findKnowledgeBaseDocument(documents, guide.steps[step + 1])
    : undefined
  const updated = formatDate(data.lastModified)
  const showToc = rendered.headings.length > WIDE_TOC_THRESHOLD

  return (
    <article>
      <div
        className="sticky top-0 z-10 h-0.5 origin-left bg-linear-to-r from-accent-100 to-brand-2"
        style={{ transform: `scaleX(${progress})` }}
      />
      <header className="border-b border-neutral-10 px-6 pt-16 pb-4.5">
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-neutral-50">
          {category && (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 font-semibold',
                category.tone
              )}
            >
              {category.label}
            </span>
          )}
          <KnowledgeBaseSourceTag path={path} />
          <span className="inline-flex items-center gap-1">
            <ClockIcon className="size-3.5" />
            {rendered.readingMinutes} min read
          </span>
          {updated && <span>· Updated {updated}</span>}
          <a
            href={knowledgeBasePdfUrl(path)}
            download
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-hairline px-2.5 py-1 font-medium text-neutral-70 transition-colors hover:border-accent-30 hover:bg-accent-05 hover:text-accent-100"
          >
            <ArrowDownTrayIcon className="size-3.5" />
            Download PDF
          </a>
        </div>
        <h1 className="mt-2.5 text-[24px] leading-tight font-semibold tracking-tighter">
          {rendered.title}
        </h1>
        {rendered.lead && (
          <p
            className="mt-2 text-[14px] leading-relaxed text-neutral-50"
            dangerouslySetInnerHTML={{ __html: rendered.lead }}
          />
        )}
      </header>

      <div
        className={cn(
          'grid',
          isWide && showToc
            ? 'grid-cols-[minmax(0,1fr)_210px] gap-7 pr-5'
            : 'grid-cols-1'
        )}
      >
        <div className="min-w-0">
          {showToc && !isWide && (
            <details className="group mx-6 mt-4 rounded-xl border border-hairline">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-[12.5px] font-semibold text-neutral-70 [&::-webkit-details-marker]:hidden">
                <DocumentTextIcon className="size-3.5" />
                On this page
                <span className="font-normal text-neutral-45">
                  {rendered.headings.length} sections
                </span>
                <ChevronDownIcon className="ml-auto size-3.5 transition-transform group-open:rotate-180" />
              </summary>
              <ol className="columns-2 gap-4 px-3 pb-2.5">
                {rendered.headings.map((heading) => (
                  <li key={heading.id} className="break-inside-avoid">
                    <a
                      href={`#${heading.id}`}
                      data-kb-anchor={heading.id}
                      onClick={(event) => {
                        event.preventDefault()
                        event.currentTarget
                          .closest('details')
                          ?.removeAttribute('open')
                        scrollToAnchor(heading.id, true)
                      }}
                      className="block py-1 text-[12.5px] leading-snug text-neutral-50 hover:text-accent-100"
                    >
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ol>
            </details>
          )}

          <div
            className="kb-prose"
            onClick={onContentClick}
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />

          <footer className="mx-6 mt-9 border-t border-neutral-10 pt-3.5 pb-8">
            {guide && (
              <div className="mb-4">
                <div className="mb-2 flex items-center gap-2 text-[11.5px] text-neutral-45">
                  {guide.title} · step {step + 1} of {guide.steps.length}
                  <span className="h-[3px] flex-1 overflow-hidden rounded-full bg-neutral-10">
                    <span
                      className="block h-full rounded-full bg-accent-100"
                      style={{
                        width: `${((step + 1) / guide.steps.length) * 100}%`,
                      }}
                    />
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {previous && (
                    <button
                      type="button"
                      onClick={() => onNavigate(previous.path)}
                      className="rounded-xl border border-hairline px-3 py-2.5 text-left transition-colors hover:border-accent-30 hover:bg-accent-05"
                    >
                      <span className="block text-[11.5px] text-neutral-45">
                        ← Previous
                      </span>
                      <span className="block text-[13px] font-semibold">
                        {previous.title}
                      </span>
                    </button>
                  )}
                  {next && (
                    <button
                      type="button"
                      onClick={() => onNavigate(next.path)}
                      className="col-start-2 rounded-xl border border-hairline px-3 py-2.5 text-right transition-colors hover:border-accent-30 hover:bg-accent-05"
                    >
                      <span className="block text-[11.5px] text-neutral-45">
                        Next →
                      </span>
                      <span className="block text-[13px] font-semibold">
                        {next.title}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}
            <a
              href={knowledgeBasePublicSiteUrl(path)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12.5px] font-medium text-neutral-50 transition-colors hover:text-accent-100"
            >
              Open the Inter.link Knowledge Base
              <ArrowTopRightOnSquareIcon className="size-3.5" />
            </a>
          </footer>
        </div>

        {isWide && showToc && (
          <nav
            aria-label="On this page"
            className="sticky top-4 self-start pt-6"
          >
            <p className="mb-2 text-[11px] font-semibold tracking-wider text-neutral-45 uppercase">
              On this page
            </p>
            {rendered.headings.map((heading) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                onClick={(event) => {
                  event.preventDefault()
                  scrollToAnchor(heading.id, true)
                }}
                className={cn(
                  'block border-l-2 py-1.5 pl-3 text-[12.5px] leading-snug transition-colors',
                  activeHeading === heading.id
                    ? 'border-accent-100 font-medium text-accent-100'
                    : 'border-hairline text-neutral-50 hover:text-accent-100'
                )}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        )}
      </div>

      {lightbox &&
        createPortal(
          <button
            type="button"
            aria-label="Close image"
            onClick={() => setLightbox(undefined)}
            className="fixed inset-0 z-[70] grid cursor-zoom-out place-items-center bg-auxiliary-3/80 p-10 backdrop-blur-sm"
          >
            <img
              src={lightbox}
              alt=""
              className="max-h-full max-w-full rounded-xl shadow-popover"
            />
          </button>,
          document.body
        )}
    </article>
  )
}

export default KnowledgeBaseArticle
